import type { Melody, MelodyChordLane, MelodyEntityNote, MelodyMeter, Scale } from '@tunasong/schemas'
import { chordTones } from '../chord/chord-tones.js'
import { laneEnd } from './chord-lane.js'
import { barBeats, GRID_TOLERANCE } from './grid.js'
import { keyPitchClasses, noteRoles } from './note-roles.js'

/**
 * The selection actions (melody toolkit M6; spec §5.4): deterministic, instant and free. Each returns the melody's
 * notes as they would be after Keep, and which of them are now the selection, or null when the action is unavailable
 * for that selection. Nothing here writes: the result is shown as a ghost option (spec §6) until Keep.
 *
 * Bowie's rules and fixtures (2026-10-23, updated 2026-10-30), with Brian's calls:
 * - `Repeat`, `Repeat, fit to chords` and `Sequence` add a COPY after the selection. It starts at the first onset plus
 *   the selection's length rounded up to whole bars, so it keeps its place in the bar and follows straight on. The copy
 *   replaces the notes in its span, a note sounding into the span is trimmed, and the copy becomes the selection.
 * - `Mirror` and `Shift` change the selection in place.
 * - Sequence and Mirror need a key (else unavailable, Exact intervals included). A result outside MIDI 0–127 makes an
 *   action unavailable rather than moving some of the notes.
 * - The range never clamps, folds or refuses a note: that belongs to M8a.
 *
 * `offsetMs` (the feel) and velocity are carried over; durations change only where Shift trims an overlap.
 */

export interface MelodyTransformResult {
  /** Every note of the melody after Keep, by onset */
  notes: MelodyEntityNote[]
  /** Indexes into `notes` of the copy, or of the notes moved in place */
  selection: number[]
}

export type MelodyTransformDirection = 'up' | 'down'

export interface MelodyTransformOptions {
  /** Exact ("real") intervals: every note moves by the first note's scale step, in semitones */
  exact?: boolean
}

const lowestMidi = 0
const highestMidi = 127

const mod12 = (value: number) => ((value % 12) + 12) % 12
const endOf = (note: MelodyEntityNote) => note.onset + note.duration
const byOnset = (a: MelodyEntityNote, b: MelodyEntityNote) => a.onset - b.onset

/** The selected indexes that name a note, once each, in the order the notes sound */
const selectedIndexes = (melody: Melody, selection: number[]) =>
  [...new Set(selection)]
    .filter(index => melody.notes[index] !== undefined)
    .sort((a, b) => (melody.notes[a]?.onset ?? 0) - (melody.notes[b]?.onset ?? 0) || a - b)

const notesAt = (melody: Melody, indexes: number[]) =>
  indexes.flatMap(index => {
    const note = melody.notes[index]
    return note ? [note] : []
  })

/** How far a copy is placed: the selection's length, first onset to last end, rounded up to whole bars */
export const copyOffset = (notes: MelodyEntityNote[], meter: MelodyMeter): number => {
  const bar = barBeats(meter)
  const first = Math.min(...notes.map(note => note.onset))
  const last = Math.max(...notes.map(endOf))
  return Math.max(1, Math.ceil((last - first) / bar - GRID_TOLERANCE)) * bar
}

/**
 * The melody after Keep with `copied` written into it (spec §6, 4a): they replace the notes that start inside their
 * span, a note sounding into the span is trimmed to end at its start, and they become the selection. The copies here,
 * and Keep going's continuation (M7.2).
 */
export const placeNotes = (melody: Melody, copied: MelodyEntityNote[]): MelodyTransformResult => {
  const start = Math.min(...copied.map(note => note.onset))
  const end = Math.max(...copied.map(endOf))
  const kept = melody.notes.flatMap(note => {
    if (note.onset > start - GRID_TOLERANCE && note.onset < end - GRID_TOLERANCE) {
      return []
    }
    if (note.onset < start - GRID_TOLERANCE && endOf(note) > start + GRID_TOLERANCE) {
      return [{ ...note, duration: start - note.onset }]
    }
    return [note]
  })
  const notes = [...kept, ...copied].sort(byOnset)
  return { notes, selection: copied.map(note => notes.indexOf(note)) }
}

/** The selection, copied after itself with these pitches (the selection's own, unless given) */
const placeCopy = (melody: Melody, indexes: number[], pitches?: number[]): MelodyTransformResult => {
  const source = notesAt(melody, indexes)
  const offset = copyOffset(source, melody.tempo.meter)
  return placeNotes(
    melody,
    source.map((note, index) => ({ ...note, onset: note.onset + offset, midi: pitches?.[index] ?? note.midi }))
  )
}

/** `Repeat`: the selection again, straight after it. It never fixes notes (FT8) */
export const repeatSelection = (melody: Melody, selection: number[]): MelodyTransformResult | null => {
  const indexes = selectedIndexes(melody, selection)
  return indexes.length === 0 ? null : placeCopy(melody, indexes)
}

const directionOf = (from: number, to: number) => Math.sign(to - from)

/**
 * Among the chord tones within a major 3rd, the one to move to. Contour first: the nearest whose direction from the
 * previous output note matches the source's direction there, else the nearest. A tie goes the source's way; on the
 * copy's first note, where there is no direction, a tie goes lower (a clash conventionally resolves down).
 */
const fitTone = (pitch: number, candidates: number[], previousOutput: number | undefined, direction: number | null) => {
  const matching =
    direction === null || previousOutput === undefined
      ? []
      : candidates.filter(candidate => directionOf(previousOutput, candidate) === direction)
  const pool = matching.length > 0 ? matching : candidates
  const upwards = direction !== null && direction > 0
  return pool.reduce<number | undefined>((best, candidate) => {
    if (best === undefined) {
      return candidate
    }
    const distance = Math.abs(candidate - pitch)
    const bestDistance = Math.abs(best - pitch)
    if (distance !== bestDistance) {
      return distance < bestDistance ? candidate : best
    }
    return upwards ? Math.max(best, candidate) : Math.min(best, candidate)
  }, undefined)
}

/** The chord's tones within a major 3rd of `pitch`, in any octave, never an octave away */
const tonesNear = (pitch: number, symbol: string) => {
  const pitchClasses = new Set((chordTones(symbol) ?? []).map(tone => tone.pitchClass))
  return [-4, -3, -2, -1, 1, 2, 3, 4]
    .map(step => pitch + step)
    .filter(candidate => candidate >= lowestMidi && candidate <= highestMidi && pitchClasses.has(mod12(candidate)))
}

/**
 * `Repeat, fit to chords`: Repeat, then one pass over the copy, judged once at its new onsets in the after-Keep context
 * (K1). A note moves if it is an avoid note, or an accented outside note (accented as M3 has it: on a strong beat, or
 * held longer than a felt beat). Chord tones, tensions, blue notes, suspensions, leans, passing notes and neighbours
 * never move, and neither does a note with no chord under it.
 */
export const repeatFitToChords = (
  melody: Melody,
  selection: number[],
  lane: MelodyChordLane,
  key: Scale | undefined
): MelodyTransformResult | null => {
  const indexes = selectedIndexes(melody, selection)
  if (indexes.length === 0) {
    return null
  }
  const source = notesAt(melody, indexes)
  const repeated = placeCopy(melody, indexes)
  const roles = noteRoles({ ...melody, notes: repeated.notes }, lane, key)

  const output: number[] = []
  repeated.selection.forEach((noteIndex, position) => {
    const pitch = repeated.notes[noteIndex]?.midi ?? 0
    const role = roles.find(entry => entry.noteIndex === noteIndex)
    const isClash = role?.role === 'avoid' || (role?.role === 'outside' && role.accent !== undefined)
    if (!role?.chord || !isClash) {
      output.push(pitch)
      return
    }
    const before = source[position - 1]
    const direction = before ? directionOf(before.midi, source[position]?.midi ?? before.midi) : null
    output.push(fitTone(pitch, tonesNear(pitch, role.chord), output[position - 1], direction) ?? pitch)
  })
  return placeCopy(melody, indexes, output)
}

/** Every MIDI pitch in the key, low to high */
const ladderOf = (key: Scale) => {
  const pitchClasses = new Set(keyPitchClasses(key))
  return Array.from({ length: highestMidi + 1 }, (_, midi) => midi).filter(midi => pitchClasses.has(mod12(midi)))
}

/**
 * Place the chromatic notes against the moved scale notes, the first rule that applies, working back from the end so
 * each note is placed against one already moved:
 * 1. a semitone from the NEXT note: a semitone from the moved next note, on the same side;
 * 2. else a semitone from the PREVIOUS note, the same against the moved previous note;
 * 3. else it keeps its interval to the nearest scale note before it, or after it if there is none before.
 * 4. A result on the same pitch as a moved neighbour is kept: a chromatic slot can't survive a move into a half step.
 * `flip` is Mirror's: the side is reflected, so a note a semitone below its target lands a semitone above it.
 * Null when a chromatic note has nothing to be placed against.
 */
const placeChromatic = (source: number[], moved: (number | undefined)[], isScale: boolean[], flip: boolean) => {
  const side = flip ? -1 : 1
  const placed = [...moved]
  for (let index = source.length - 1; index >= 0; index -= 1) {
    if (isScale[index]) {
      continue
    }
    const pitch = source[index] ?? 0
    const against = (other: number) => {
      const otherMoved = placed[other]
      const otherPitch = source[other]
      return otherMoved === undefined || otherPitch === undefined ? undefined : otherMoved + side * (pitch - otherPitch)
    }
    const isSemitoneFrom = (other: number) =>
      source[other] !== undefined && placed[other] !== undefined && Math.abs(pitch - (source[other] ?? 0)) === 1
    const before = isScale.slice(0, index).lastIndexOf(true)
    const after = isScale.indexOf(true, index + 1)
    const anchor = before >= 0 ? before : after
    placed[index] = isSemitoneFrom(index + 1)
      ? against(index + 1)
      : isSemitoneFrom(index - 1)
        ? against(index - 1)
        : anchor >= 0
          ? against(anchor)
          : undefined
  }
  return placed
}

/** The pitches, or null when one is missing or falls outside MIDI 0–127 */
const inRange = (pitches: (number | undefined)[]): number[] | null =>
  pitches.every(pitch => pitch !== undefined && pitch >= lowestMidi && pitch <= highestMidi)
    ? (pitches as number[])
    : null

/** The first scale pitch above (`up`) or below `pitch`: a scale note's step, or a chromatic note's nearest */
const scaleStepFrom = (ladder: number[], pitch: number, direction: MelodyTransformDirection) =>
  direction === 'up' ? ladder.find(midi => midi > pitch) : [...ladder].reverse().find(midi => midi < pitch)

/**
 * Exact intervals' step, in semitones: a scale note's is to the next scale pitch that way. A chromatic note's is the
 * step it sits inside, from its lower scale neighbour to its upper, taken either way: F♯ in C sits inside F–G, so it
 * moves 2, not the semitone to its nearest scale note, which would make a chromatic slide of a step sequence (Bowie).
 */
const firstStepOf = (ladder: number[], pitch: number, direction: MelodyTransformDirection) => {
  if (ladder.includes(pitch)) {
    const next = scaleStepFrom(ladder, pitch, direction)
    return next === undefined ? undefined : next - pitch
  }
  const below = scaleStepFrom(ladder, pitch, 'down')
  const above = scaleStepFrom(ladder, pitch, 'up')
  if (below === undefined || above === undefined) {
    return undefined
  }
  return direction === 'up' ? above - below : below - above
}

/**
 * `Sequence up` / `Sequence down`: a copy a scale step higher or lower, placed as Repeat places it. Scale notes move
 * one step along the key's scale, across octaves; chromatic notes follow `placeChromatic`. With exact intervals, every
 * note moves by the first note's scale step in semitones, so the inside of the motif stays intact.
 */
export const sequenceSelection = (
  melody: Melody,
  selection: number[],
  key: Scale | undefined,
  direction: MelodyTransformDirection,
  options: MelodyTransformOptions = {}
): MelodyTransformResult | null => {
  const indexes = selectedIndexes(melody, selection)
  if (!key || indexes.length === 0) {
    return null
  }
  const ladder = ladderOf(key)
  const source = notesAt(melody, indexes).map(note => note.midi)
  const isScale = source.map(pitch => ladder.includes(pitch))
  /** With no scale note to place the chromatic ones against, by scale step falls back to exact, as Mirror does */
  if (options.exact || !isScale.includes(true)) {
    const step = firstStepOf(ladder, source[0] ?? 0, direction)
    const pitches = inRange(source.map(pitch => (step === undefined ? undefined : pitch + step)))
    return pitches ? placeCopy(melody, indexes, pitches) : null
  }
  const moved = source.map((pitch, index) => (isScale[index] ? scaleStepFrom(ladder, pitch, direction) : undefined))
  const pitches = inRange(placeChromatic(source, moved, isScale, false))
  return pitches ? placeCopy(melody, indexes, pitches) : null
}

/** The selection with these pitches, in place */
const inPlace = (melody: Melody, indexes: number[], pitches: number[]): MelodyTransformResult => {
  const byIndex = new Map(indexes.map((noteIndex, position) => [noteIndex, pitches[position]]))
  return {
    notes: melody.notes.map((note, index) => {
      const pitch = byIndex.get(index)
      return pitch === undefined ? note : { ...note, midi: pitch }
    }),
    selection: indexes,
  }
}

/**
 * `Mirror`: in place, a diatonic inversion around the first note. A scale note n steps from it goes to −n steps, with
 * no octave folding; chromatic notes follow `placeChromatic` with the side flipped. A first note outside the scale
 * leaves no axis on the scale, so the whole selection is mirrored by exact intervals, as Exact intervals always is.
 */
export const mirrorSelection = (
  melody: Melody,
  selection: number[],
  key: Scale | undefined,
  options: MelodyTransformOptions = {}
): MelodyTransformResult | null => {
  const indexes = selectedIndexes(melody, selection)
  if (!key || indexes.length === 0) {
    return null
  }
  const ladder = ladderOf(key)
  const source = notesAt(melody, indexes).map(note => note.midi)
  const first = source[0] ?? 0
  const axis = ladder.indexOf(first)
  if (options.exact || axis < 0) {
    const pitches = inRange(source.map(pitch => 2 * first - pitch))
    return pitches ? inPlace(melody, indexes, pitches) : null
  }
  const isScale = source.map(pitch => ladder.includes(pitch))
  const moved = source.map(pitch => {
    const step = ladder.indexOf(pitch)
    return step < 0 ? undefined : ladder[2 * axis - step]
  })
  const pitches = inRange(placeChromatic(source, moved, isScale, true))
  return pitches ? inPlace(melody, indexes, pitches) : null
}

/**
 * How far `Shift earlier` / `Shift later` moves, in quarter-note beats: half a beat in meters written in quarters, and a
 * whole eighth in every meter written in eighths (half a felt beat of 6/8 falls off the grid). Both are 0.5. Bowie's
 * quarter for 2/2 applies only if the meter schema ever allows halves.
 */
export const shiftStep = (_meter: MelodyMeter): number => 0.5

/**
 * `Shift earlier` / `Shift later`: the selection moves as one block. A vocal line has one note at a time, so the
 * earlier of two overlapping notes is trimmed to end where the later begins. Unavailable when a selected note would
 * start before beat 0 or end past the lane, or when a trim would leave a note with no length: a note is never deleted
 * silently. The notes are judged again afterwards by whoever shows them.
 */
export const shiftSelection = (
  melody: Melody,
  selection: number[],
  direction: 'earlier' | 'later',
  lane: MelodyChordLane
): MelodyTransformResult | null => {
  const indexes = new Set(selectedIndexes(melody, selection))
  if (indexes.size === 0) {
    return null
  }
  const delta = shiftStep(melody.tempo.meter) * (direction === 'later' ? 1 : -1)
  const end = lane.chords.length > 0 ? laneEnd(lane) : Infinity
  const shifted = melody.notes.map((note, index) => ({
    note: indexes.has(index) ? { ...note, onset: note.onset + delta } : note,
    isSelected: indexes.has(index),
  }))
  const moved = shifted.filter(entry => entry.isSelected).map(entry => entry.note)
  if (moved.some(note => note.onset < -GRID_TOLERANCE || endOf(note) > end + GRID_TOLERANCE)) {
    return null
  }
  const ordered = [...shifted].sort((a, b) => byOnset(a.note, b.note))
  const trimmed = ordered.map((entry, position) => {
    const next = ordered[position + 1]
    if (!next || endOf(entry.note) <= next.note.onset + GRID_TOLERANCE) {
      return entry
    }
    return { ...entry, note: { ...entry.note, duration: next.note.onset - entry.note.onset } }
  })
  if (trimmed.some(entry => entry.note.duration < GRID_TOLERANCE)) {
    return null
  }
  return {
    notes: trimmed.map(entry => entry.note),
    selection: trimmed.flatMap((entry, position) => (entry.isSelected ? [position] : [])),
  }
}
